import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ACCESS_TOKEN_PORTAL_KEY } from '../../core/constants/storage-keys';
import { ViaCepResponse } from '../../core/models/cep';
import { DocumentoRequerimentoApi, DocumentoRequerimentoEnviadoApi } from '../../core/models/documento';
import {
  BeneficiarioApi,
  BeneficiarioPatchPayload,
  BeneficiarioPayload,
  BeneficioApi,
  BeneficioFormulario,
  CadastroApi,
  DadosCadastraisBase,
  DadosCadastraisBaseApi,
  GrauParentescoApi,
  Home,
  HomeApi,
  Requerimento,
  RequerimentoApi,
  Resumo,
  ResumoApi,
  ResumoValores,
  SaldosRequerimento,
  SaldosRequerimentoApi,
  SimulacaoBeneficio,
  SimulacaoBeneficioApi,
  Termo,
  TermoApi,
  TributacaoApi,
  TributacaoSimulada,
  TributacaoSimuladaApi,
} from '../../core/models/requerimento';
import {
  fromApiDadosCadastraisBase,
  fromApiHome,
  fromApiRequerimento,
  fromApiResumo,
  fromApiSaldosRequerimento,
  fromApiSimulacaoBeneficio,
  fromApiTermo,
  fromApiTributacaoSimulada,
  toResumoPayload,
  toSimulacaoParams,
} from '../../core/models/requerimento-mapper';
import { LocalStorageService } from './local-storage-service';

export type BlocoRequerimento =
  | { cadastro: Partial<CadastroApi> }
  | { beneficio: Partial<BeneficioApi> }
  | { tributacao: Partial<TributacaoApi> };

export interface EtapasRequerimento {
  cadastro: Partial<CadastroApi>;
  beneficio: Partial<BeneficioApi>;
  tributacao: Partial<TributacaoApi>;
}

@Injectable({
  providedIn: 'root',
})
export class RequerimentoService {
  constructor(
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService,
  ) {}

  /** Abre (ou devolve o já aberto) rascunho do requerimento do tipo de instituto e do benefício. */
  async abrir(idTpInst: number, idBeneficio: number): Promise<Requerimento> {
    const headers = this.getHeaders();
    const url = this.getUrlRequerimentos();
    const resposta = await lastValueFrom(
      this.httpClient.post<RequerimentoApi>(url, { id_tp_inst: idTpInst, id_beneficio: idBeneficio }, { headers }),
    );
    return fromApiRequerimento(resposta);
  }

  /** Etapa 0: tudo vem do token, então não há parâmetros. */
  async getHome(): Promise<Home> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/home`;
    const resposta = await lastValueFrom(this.httpClient.get<HomeApi>(url, { headers }));
    return fromApiHome(resposta);
  }

  async listar(): Promise<Requerimento[]> {
    const headers = this.getHeaders();
    const url = this.getUrlRequerimentos();
    const resposta = await lastValueFrom(this.httpClient.get<RequerimentoApi[]>(url, { headers }));
    return resposta.map(fromApiRequerimento);
  }

  async getPorId(id: number): Promise<Requerimento> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}`;
    const resposta = await lastValueFrom(this.httpClient.get<RequerimentoApi>(url, { headers }));
    return fromApiRequerimento(resposta);
  }

  async salvar(id: number, bloco: BlocoRequerimento): Promise<Requerimento> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}`;
    const resposta = await lastValueFrom(
      this.httpClient.patch<RequerimentoApi>(url, bloco, { headers }),
    );
    return fromApiRequerimento(resposta);
  }

  /**
   * Lê os dados vigentes da base unificada para a etapa 1 e grava a origem que o envio compara (PRE-176/177).
   * `substituir` refaz o rascunho com a base: é o caminho depois de o envio recusar por divergência.
   */
  async carregarDadosCadastrais(id: number, substituir = false): Promise<void> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/dados-cadastrais/carregar`;
    const params = new HttpParams().set('substituir', String(substituir));
    await lastValueFrom(this.httpClient.post<unknown>(url, null, { headers, params }));
  }

  /**
   * Dados vigentes da base unificada para a etapa 1 (cadastro, contas, bancos e países), sem requerimento e sem gravar
   * nada. Sem cadastro na base, a API responde 409.
   */
  async obterDadosCadastraisBase(): Promise<DadosCadastraisBase> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/dados-cadastrais-base`;
    const resposta = await lastValueFrom(this.httpClient.get<DadosCadastraisBaseApi>(url, { headers }));
    return fromApiDadosCadastraisBase(resposta);
  }

  async substituir(id: number, etapas: EtapasRequerimento): Promise<Requerimento> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}`;
    const resposta = await lastValueFrom(
      this.httpClient.put<RequerimentoApi>(url, etapas, { headers }),
    );
    return fromApiRequerimento(resposta);
  }

  async listarBeneficiarios(id: number): Promise<BeneficiarioApi[]> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/beneficiarios`;
    return lastValueFrom(this.httpClient.get<BeneficiarioApi[]>(url, { headers }));
  }

  async incluirBeneficiario(id: number, beneficiario: BeneficiarioPayload): Promise<BeneficiarioApi> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/beneficiarios`;
    return lastValueFrom(this.httpClient.post<BeneficiarioApi>(url, beneficiario, { headers }));
  }

  /** Atualização parcial; `{ fl_ativo: false }` remove o beneficiário da lista. */
  async atualizarBeneficiario(
    id: number,
    idBeneficiario: number,
    alteracoes: BeneficiarioPatchPayload,
  ): Promise<BeneficiarioApi> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/beneficiarios/${idBeneficiario}`;
    return lastValueFrom(this.httpClient.patch<BeneficiarioApi>(url, alteracoes, { headers }));
  }

  /** Endereço do CEP no ViaCEP (API pública, sem headers). CEP inexistente vem com 200 e { erro: true }. */
  async consultarCep(cep: string): Promise<ViaCepResponse> {
    const url = `https://viacep.com.br/ws/${cep}/json/`;
    return lastValueFrom(this.httpClient.get<ViaCepResponse>(url));
  }

  async listarGrausParentesco(): Promise<GrauParentescoApi[]> {
    const headers = this.getHeadersBaseUnificada();
    const url = `${environment.baseUrl}/${environment.baseUnificadaParametrosUrl}/graus-parentesco`;
    return lastValueFrom(this.httpClient.get<GrauParentescoApi[]>(url, { headers }));
  }

  /**
   * Envia um documento do requerimento como multipart/form-data: `id_doc_tipo` é o id do tipo de documento
   * (catálogo da parametrização) e `arquivo` o arquivo, de até 10 MB. Devolve a situação da análise.
   */
  async enviarDocumento(id: number, idTipoDocumento: number, arquivo: File): Promise<DocumentoRequerimentoApi> {
    // Sem Content-Type: com FormData o navegador monta o multipart/form-data com o boundary.
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/documentos`;
    const body = new FormData();
    body.append('id_doc_tipo', String(idTipoDocumento));
    body.append('arquivo', arquivo, arquivo.name);
    return lastValueFrom(this.httpClient.post<DocumentoRequerimentoApi>(url, body, { headers }));
  }

  /** Envio atual (vigente) de cada tipo de documento, com a situação da análise; sem o histórico das recusas. */
  async listarDocumentos(id: number): Promise<DocumentoRequerimentoEnviadoApi[]> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/documentos`;
    const params = new HttpParams().set('vigente', 'S');
    return lastValueFrom(this.httpClient.get<DocumentoRequerimentoEnviadoApi[]>(url, { headers, params }));
  }

  /**
   * Remove o envio vigente do documento (remoção lógica, o arquivo fica como histórico) e invalida o aceite do termo.
   * Só no rascunho: fora dele, ou com envio já substituído, a API responde 409.
   */
  async removerDocumento(id: number, idDocumento: number): Promise<void> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/documentos/${idDocumento}`;
    await lastValueFrom(this.httpClient.delete<void>(url, { headers }));
  }

  async cancelar(id: number): Promise<Requerimento> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/status`;
    const resposta = await lastValueFrom(
      this.httpClient.patch<RequerimentoApi>(url, { tp_status: 'C' }, { headers }),
    );
    return fromApiRequerimento(resposta);
  }

  /**
   * Simula o IR do benefício salvo na etapa 2, por regime. Não grava nada. Etapa 2 incompleta: 400; simulador
   * indisponível: 503. Sem `qtdDependentes`, a API usa o valor salvo no requerimento.
   */
  async simularTributacao(id: number, qtdDependentes: number | null = null): Promise<TributacaoSimulada> {
    return this.simularIr(`${id}/tributacao`, qtdDependentes);
  }

  /**
   * Etapa 3: IR do benefício mensal, por regime, sem o 1º pagamento da antecipação. Não chamar em paralelo com
   * `simularTributacaoAntecipacao`: o simulador grava a projeção por participante e as duas se atropelam.
   */
  async simularTributacaoBeneficio(id: number, qtdDependentes: number | null = null): Promise<TributacaoSimulada> {
    return this.simularIr(`${id}/tributacao/beneficio`, qtdDependentes);
  }

  /**
   * Etapa 3: IR da antecipação de saldo, por regime. Sem antecipação configurada na etapa 2, a API responde 400. Não
   * chamar em paralelo com `simularTributacaoBeneficio`.
   */
  async simularTributacaoAntecipacao(id: number, qtdDependentes: number | null = null): Promise<TributacaoSimulada> {
    return this.simularIr(`${id}/tributacao/antecipacao`, qtdDependentes);
  }

  private async simularIr(caminho: string, qtdDependentes: number | null): Promise<TributacaoSimulada> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${caminho}`;
    const params =
      qtdDependentes === null
        ? new HttpParams()
        : new HttpParams().set('qtd_depend_ir', String(qtdDependentes));
    const resposta = await lastValueFrom(
      this.httpClient.get<TributacaoSimuladaApi>(url, { headers, params }),
    );
    return fromApiTributacaoSimulada(resposta);
  }

  /**
   * Etapa 2: saldos do participante no plano e os limites de antecipação e opções de renda. Não grava nada. Plano sem
   * parametrização: 404; Hub ou parametrização indisponível: 503.
   */
  async getSaldos(id: number): Promise<SaldosRequerimento> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/saldos`;
    const resposta = await lastValueFrom(this.httpClient.get<SaldosRequerimentoApi>(url, { headers }));
    return fromApiSaldosRequerimento(resposta);
  }

  /**
   * Etapa 2: recalcula o benefício com a modalidade escolhida na tela, sem gravar. Benefício fora das faixas: 400;
   * simulador indisponível: 503.
   */
  async simularBeneficio(id: number, formulario: BeneficioFormulario): Promise<SimulacaoBeneficio> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/simulacao`;
    const params = new HttpParams({ fromObject: toSimulacaoParams(formulario) });
    const resposta = await lastValueFrom(
      this.httpClient.get<SimulacaoBeneficioApi>(url, { headers, params }),
    );
    return fromApiSimulacaoBeneficio(resposta);
  }

  /** Snapshot dos valores da etapa 6; sem resumo gravado, a API responde 404. */
  async getResumo(id: number): Promise<Resumo> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/resumo`;
    const resposta = await lastValueFrom(this.httpClient.get<ResumoApi>(url, { headers }));
    return fromApiResumo(resposta);
  }

  /** Grava o primeiro snapshot; se já existe, a API responde 409 (use substituirResumo). */
  async criarResumo(id: number, valores: ResumoValores): Promise<Resumo> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/resumo`;
    const resposta = await lastValueFrom(
      this.httpClient.post<ResumoApi>(url, toResumoPayload(valores), { headers }),
    );
    return fromApiResumo(resposta);
  }

  /** Recálculo: substitui o snapshot inteiro. Congelado pela assinatura do termo, a API responde 409. */
  async substituirResumo(id: number, valores: ResumoValores): Promise<Resumo> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/resumo`;
    const resposta = await lastValueFrom(
      this.httpClient.put<ResumoApi>(url, toResumoPayload(valores), { headers }),
    );
    return fromApiResumo(resposta);
  }

  async getTermo(id: number): Promise<Termo> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/termo`;
    const resposta = await lastValueFrom(this.httpClient.get<TermoApi>(url, { headers }));
    return fromApiTermo(resposta);
  }

  /** O PDF vem binário de uma rota autenticada; quem exibe cria o endereço com URL.createObjectURL. */
  async getArquivoTermo(id: number): Promise<Blob> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/termo/arquivo`;
    return lastValueFrom(this.httpClient.get(url, { headers, responseType: 'blob' }));
  }

  async aceitarTermo(id: number, hashTermo: string): Promise<Termo> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/termo/aceite`;
    // hash_resumo é opcional até o resumo da etapa 6 existir na API (PRE-203).
    const resposta = await lastValueFrom(
      this.httpClient.post<TermoApi>(url, { hash_termo: hashTermo, hash_resumo: null }, { headers }),
    );
    return fromApiTermo(resposta);
  }

  async enviar(id: number): Promise<Requerimento> {
    const headers = this.getHeaders();
    const url = `${this.getUrlRequerimentos()}/${id}/envio`;
    const resposta = await lastValueFrom(this.httpClient.post<RequerimentoApi>(url, null, { headers }));
    return fromApiRequerimento(resposta);
  }

  private getUrlRequerimentos(): string {
    // Sem o segmento vazio: no local requerimentoBaseUrl = '' e a URL sairia com barra dupla (404 na API).
    return [environment.baseUrl, environment.requerimentoBaseUrl, 'v1/requerimentos'].filter(Boolean).join('/');
  }

  /** Listas da base unificada: não usam o token do portal, só a subscription key e a x-api-key. */
  private getHeadersBaseUnificada(): HttpHeaders {
    return new HttpHeaders({
      'Ocp-Apim-Subscription-Key': environment.ocpapimsubscriptionkeybaseUnificadaParametros,
      'x-api-key': environment.x_api_key,
    });
  }

  private getHeaders(): HttpHeaders {
    const token = this.localStorageService.get(ACCESS_TOKEN_PORTAL_KEY);
    if (!token) {
      throw new HttpErrorResponse({ status: 401, statusText: 'Missing portal token.' });
    }

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Ocp-Apim-Subscription-Key': environment.ocpapimsubscriptionkeyRequerimento,
    });
  }
}
